<template>
  <base-modal :visible="visible" width="460px" @close="$emit('close')">
    <template #title>
      <i class="fas fa-user"></i>
      <span>Водитель{{ carLabel ? ' · ' + carLabel : '' }}</span>
    </template>

    <!-- Добавление нового водителя -->
    <div v-if="mode === 'create'" class="dp-form">
      <p class="dp-hint">Новый водитель закрепится за машиной и встанет в разнарядку на эту и следующие даты.</p>
      <c-f-input v-model="form.lastname" label="Фамилия *" no-autofill :disabled="creating" />
      <c-f-input v-model="form.name" label="Имя" no-autofill :disabled="creating" />
      <c-f-input v-model="form.patronymic" label="Отчество" no-autofill :disabled="creating" />
      <c-f-input v-model="form.phone" label="Телефон" placeholder="+7 900 000-00-00" no-autofill :disabled="creating" />
      <p v-if="form.bot_user_id" class="dp-hint"><i class="fas fa-comment-dots"></i> Пользователь бота Max будет привязан к водителю и начнёт получать уведомления.</p>
      <p v-if="error" class="dp-error" role="alert"><i class="fas fa-exclamation-triangle"></i> {{ error }}</p>
      <div v-if="existing" class="dp-existing">
        <app-button variant="outline" size="sm" icon="fas fa-check" @click="pick(existing)">Выбрать: {{ existing.name }}</app-button>
      </div>
    </div>

    <!-- Выбор -->
    <template v-else>
      <c-f-input ref="search" v-model="query" icon="fas fa-search" placeholder="ФИО или телефон…" no-autofill />

      <div class="dp-list">
        <template v-if="result.pinned.length">
          <div class="dp-title">Закреплены за машиной</div>
          <button v-for="d in result.pinned" :key="'p' + d.id" type="button" class="dp-item" :class="{ 'is-current': isCurrent(d) }" @click="pick(d)">
            <span class="dp-name">{{ d.name }}</span>
            <span class="dp-sub">
              <span v-if="d.main" class="dp-badge dp-badge--main">основной</span>
              <span v-if="d.in_bot" class="dp-badge">в боте</span>
              <span v-if="d.phone">{{ d.phone }}</span>
            </span>
          </button>
        </template>

        <template v-if="result.drivers.length">
          <div class="dp-title">Водители</div>
          <button v-for="d in result.drivers" :key="'d' + d.id" type="button" class="dp-item" :class="{ 'is-current': isCurrent(d) }" @click="pick(d)">
            <span class="dp-name">{{ d.name }}</span>
            <span class="dp-sub">
              <span v-if="d.in_bot" class="dp-badge">в боте</span>
              <span v-if="d.phone">{{ d.phone }}</span>
            </span>
          </button>
        </template>

        <template v-if="result.bots.length">
          <div class="dp-title">Пользователи бота Max (ещё не водители)</div>
          <button v-for="b in result.bots" :key="'b' + b.bot_id" type="button" class="dp-item" @click="startCreate(b)">
            <span class="dp-name">{{ b.name }}</span>
            <span class="dp-sub">
              <span class="dp-badge dp-badge--new">добавить водителем</span>
              <span v-if="b.phone">{{ b.phone }}</span>
              <span v-if="b.max_name && b.max_name !== b.name">в Max: {{ b.max_name }}</span>
            </span>
          </button>
        </template>

        <p v-if="loading && !hasAny" class="dp-empty">Поиск…</p>
        <p v-else-if="!hasAny && searching" class="dp-empty">Ничего не найдено</p>
        <p v-else-if="!hasAny" class="dp-empty">За машиной пока нет закреплённых водителей. Начните вводить ФИО или телефон.</p>
        <p v-if="loadError" class="dp-error" role="alert"><i class="fas fa-exclamation-triangle"></i> {{ loadError }}</p>
      </div>

      <!-- Ничего не нашли — предлагаем завести; а если нашли, но нужного нет, ссылка всё равно внизу -->
      <button v-if="searching && !loading && !hasAny" type="button" class="dp-create" @click="startCreate()">
        <i class="fas fa-user-plus"></i> Добавить водителя «{{ query.trim() }}»
      </button>
      <button v-else-if="searching && !loading" type="button" class="dp-link" @click="startCreate()">
        <i class="fas fa-user-plus"></i> Нужного нет — добавить нового водителя
      </button>
    </template>

    <template #footer>
      <template v-if="mode === 'create'">
        <app-button variant="ghost" :disabled="creating" @click="backToList">Назад</app-button>
        <app-button variant="primary" icon="fas fa-user-plus" :disabled="creating || !form.lastname.trim()" @click="create">
          {{ creating ? 'Добавляю…' : 'Добавить и выбрать' }}
        </app-button>
      </template>
      <app-button v-else variant="ghost" class="dp-cancel" @click="$emit('close')">Отмена</app-button>
    </template>
  </base-modal>
</template>

<script>
import axios from 'axios'

// Поиск начинается с этого числа символов; раньше показываются только закреплённые за машиной
const MIN_QUERY = 2
const DEBOUNCE_MS = 300

// Выбор водителя в разнарядке: закреплённые за машиной, поиск по базе водителей и по пользователям бота Max, создание нового.
// Окно только выбирает (и при необходимости заводит) водителя; закрепление за машиной и проставление на будущие дни делает страница.
export default {
  name: 'DriverPickerModal',
  props: {
    visible: { type: Boolean, default: false },
    carId: { type: [Number, String], default: null },
    carLabel: { type: String, default: '' },
    // текущий водитель строки (ФИО) — подсвечивается
    value: { type: String, default: '' }
  },
  data() {
    return {
      query: '',
      loading: false,
      loadError: '',
      result: { pinned: [], drivers: [], bots: [] },
      mode: 'list',
      form: { lastname: '', name: '', patronymic: '', phone: '', bot_user_id: null },
      creating: false,
      error: '',
      existing: null,
      timer: null,
      seq: 0
    }
  },
  computed: {
    searching() {
      return this.query.trim().length >= MIN_QUERY
    },
    hasAny() {
      return this.result.pinned.length + this.result.drivers.length + this.result.bots.length > 0
    }
  },
  watch: {
    visible(v) {
      if (v) this.open()
      else clearTimeout(this.timer)
    },
    query() {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.load, DEBOUNCE_MS)
    }
  },
  mounted() {
    if (this.visible) this.open()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    open() {
      this.query = ''
      this.mode = 'list'
      this.error = ''
      this.existing = null
      this.result = { pinned: [], drivers: [], bots: [] }
      this.load()
      this.$nextTick(() => {
        const input = this.$refs.search && this.$refs.search.$el && this.$refs.search.$el.querySelector('input')
        if (input) input.focus()
      })
    },
    load() {
      const q = this.searching ? this.query.trim() : ''
      const seq = ++this.seq
      this.loading = true
      this.loadError = ''
      axios.get('/drivers/search', { params: { car: this.carId || undefined, q: q || undefined } })
        .then((res) => {
          // устаревший ответ (пользователь уже набрал дальше) не показываем
          if (seq !== this.seq) return
          const d = res.data || {}
          this.result = { pinned: d.pinned || [], drivers: d.drivers || [], bots: d.bots || [] }
        })
        .catch((err) => {
          if (seq !== this.seq) return
          const data = err && err.response && err.response.data
          this.loadError = (data && (data.error || data.message)) || 'Не удалось выполнить поиск'
        })
        .finally(() => {
          if (seq === this.seq) this.loading = false
        })
    },
    isCurrent(d) {
      return !!this.value && d.name === this.value
    },
    pick(d) {
      this.$emit('pick', { id: d.id, name: d.name })
      this.$emit('close')
    },
    backToList() {
      this.mode = 'list'
      this.error = ''
      this.existing = null
    },

    // Форма нового водителя: ФИО и телефон подставляются из набранного или из пользователя бота
    startCreate(bot) {
      const form = { lastname: '', name: '', patronymic: '', phone: '', bot_user_id: null }
      const source = bot ? bot.name : this.query.trim()
      const words = source.replace(/[\d+()-]+/g, ' ').split(/\s+/).filter(Boolean)
      form.lastname = words[0] || ''
      form.name = words[1] || ''
      form.patronymic = words.slice(2).join(' ')
      if (bot) {
        form.phone = bot.phone || ''
        form.bot_user_id = bot.bot_id
      } else {
        const digits = this.query.replace(/\D/g, '')
        if (digits.length >= 6) form.phone = this.query.replace(/[^\d+()\-\s]/g, '').trim()
      }
      this.form = form
      this.error = ''
      this.existing = null
      this.mode = 'create'
    },
    create() {
      if (this.creating || !this.form.lastname.trim()) return
      this.creating = true
      this.error = ''
      this.existing = null
      axios.post('/drivers/quick', this.form)
        .then((res) => {
          if (res.data && res.data.driver) this.pick(res.data.driver)
        })
        .catch((err) => {
          const data = err && err.response && err.response.data
          this.error = (data && (data.error || data.message)) || 'Не удалось добавить водителя'
          if (data && data.driver) this.existing = data.driver
        })
        .finally(() => {
          this.creating = false
        })
    }
  }
}
</script>

<style scoped>
.dp-list { margin-top: var(--space-8); max-height: 340px; overflow-y: auto; display: flex; flex-direction: column; gap: var(--space-0-5); }
.dp-title { padding: var(--space-4) var(--space-8) var(--space-2); font-size: var(--text-xs); font-weight: var(--font-weight-semibold); color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.04em; }
.dp-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-1);
  padding: var(--space-5) var(--space-8);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-base);
}
.dp-item:hover { background: var(--bg-selected); }
.dp-item.is-current { background: var(--bg-selected); }
.dp-name { font-size: var(--text-base); color: var(--text-primary); }
.dp-item.is-current .dp-name { font-weight: var(--font-weight-semibold); }
.dp-sub { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-4); font-size: var(--text-xs); color: var(--text-tertiary); }
.dp-badge { padding: 0 var(--space-4); border-radius: var(--radius-pill); background: var(--bg-subtle); color: var(--text-secondary); font-weight: var(--font-weight-semibold); }
.dp-badge--main { background: var(--primary-soft); color: var(--primary); }
.dp-badge--new { background: var(--success-soft); color: var(--success-text); }
.dp-empty { margin: 0; padding: var(--space-5) var(--space-8); font-style: italic; color: var(--text-secondary); font-size: var(--text-base); }
.dp-error { margin: var(--space-4) 0 0; padding: var(--space-4) var(--space-6); border-radius: var(--radius-md); background: var(--danger-soft); color: var(--danger-text); font-size: var(--text-sm); }
.dp-create {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-6);
  width: 100%;
  margin-top: var(--space-8);
  padding: var(--space-6) var(--space-8);
  border: var(--border-width-default) var(--border-style-base) var(--primary);
  border-radius: var(--radius-md);
  background: var(--primary-soft);
  color: var(--primary);
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}
.dp-create:hover { filter: brightness(0.97); }
.dp-link { margin-top: var(--space-6); border: none; background: none; color: var(--primary); font-family: inherit; font-size: var(--text-sm); font-weight: var(--font-weight-semibold); cursor: pointer; }
.dp-link:hover { text-decoration: underline; }
.dp-form { display: flex; flex-direction: column; gap: var(--space-6); }
.dp-hint { margin: 0; font-size: var(--text-xs); color: var(--text-tertiary); }
.dp-existing { display: flex; }
.dp-cancel { width: 100%; }
</style>
